import React from 'react';
import styled from 'styled-components/macro';
import BarSegment from './BarSegment';
import NPS from './NPS';
import Sentiments from '../Sentiments';
import { getTranslation, LocaleType } from '../utils/getTranslations';

export interface LabelType {
  en?: string;
  de?: string;
  fr?: string;
  it?: string;
}

export interface DataType {
  label: LabelType | string;
  sentimentMap?: null | LabelType | string;
  value: string;
  sentiment: string;
}

interface SentimentGroup {
  sentiment: string;
  label: string | LabelType;
  data: DataType[];
}

interface Props {
  data: DataType[];
  locale: LocaleType;
  nps?: number;
  isDetailed?: boolean;
}

interface SegmentType {
  sentiment: string;
  data: DataType[];
  label: LabelType | string;
}

const Chart = styled.div<{ isNPS: boolean; height?: number }>`
  display: flex;
  height: ${({ height }) => (height ? `calc(${height}px + 12rem )` : '22rem')};
  min-height: 18rem;
  margin-left: ${({ isNPS }) => (isNPS ? '3.25rem' : 0)};
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
`;
const NPSWrapper = styled.div<{ isNPS: boolean }>`
  width: ${({ isNPS }) => (isNPS ? '25%' : 0)};
  display: flex;
  align-items: center;
`;
const Line = styled.div<{ height?: number }>`
  height: ${({ height }) => (height ? `calc(${height}px + 12rem )` : '22rem')};
  min-height: 15rem;
  width: 2px;
  background-color: #e3e5e8;
  margin-left: 1rem;
`;

const groupBySentiment = (data: DataType[]) => {
  const groups: SentimentGroup[] = [];

  data.forEach((singleData: DataType) => {
    const { sentiment, sentimentMap, label } = singleData;
    const groupIndex = groups.findIndex(
      (group) => group.sentiment === sentiment
    );

    const isSentimentGroupCreated = groupIndex > -1;

    if (isSentimentGroupCreated) {
      groups[groupIndex].data.push(singleData);
    } else {
      groups.push({
        sentiment: sentiment,
        data: [singleData],
        label: sentimentMap ? sentimentMap : label,
      });
    }
  });

  return groups;
};

const BarChart = ({ data, locale = 'en', nps, isDetailed }: Props) => {
  const groupedBySentiment = groupBySentiment(data);
  const isNPS = typeof nps === 'number';
  const hasDontKnowSegmentInData = !!groupedBySentiment.find(
    (group) => Number(group.sentiment) === 99
  );
  const getBarWidth = () => {
    if (isDetailed) {
      return data.length <= 6 ? '7rem' : '3.2rem';
    } else if (isNPS) {
      return '1.25rem';
    } else {
      return '2.5rem';
    }
  };

  const maxValue = data
    .map((seg) => Number(seg.value))
    .sort((a, b) => b - a)[0];

  return (
    <Wrapper>
      {isNPS && (
        <NPSWrapper isNPS={isNPS}>
          <NPS score={nps} />
        </NPSWrapper>
      )}
      {isNPS && <Line height={maxValue} />}
      <Chart isNPS={isNPS} height={maxValue}>
        {groupedBySentiment.map((segment: SegmentType) => {
          const segmentLabel = getTranslation(segment.label, locale);

          const dontKnowSegment = 99;
          const isDontKnowSegment =
            Number(segment.sentiment) === dontKnowSegment;
          const segmentBgColor = Sentiments.get(parseInt(segment.sentiment))
            .color;
          const segmentPercentageSum = segment.data
            .map((seg) => Number(seg.value))
            .reduce((valueA, valueB) => Number(valueA) + Number(valueB));

          return (
            <BarSegment
              key={segmentLabel}
              segmentLabel={segmentLabel || ''}
              segmentPercentValue={segmentPercentageSum}
              data={segment.data}
              segmentBgColor={isDontKnowSegment ? '#C7CAD1' : segmentBgColor}
              isDontKnowSegment={isDontKnowSegment}
              isNPS={isNPS}
              locale={locale}
              hasDontKnowSegmentInData={hasDontKnowSegmentInData}
              isDetailed={isDetailed}
              width={getBarWidth()}
            />
          );
        })}
      </Chart>
    </Wrapper>
  );
};

export default BarChart;
